import { ShieldCheck, Monitor, Printer, Network, Cctv, ClipboardCheck, Wrench } from "lucide-react";
import Seo from "@/components/Seo";
import { PageHero, SectionHead, splitRows, CARD_MAX_W } from "@/components/blocks";
import { LeadForm } from "@/components/LeadForm";
import { Reveal } from "@/components/Reveal";
import { COMPANY } from "@/data/site";
import { breadcrumbJsonLd } from "@/lib/seo";

const BENEFITS = [
  { title: "Reduce unexpected downtime", text: "Regular attention helps catch problems before they interrupt your work." },
  { title: "Identify problems earlier", text: "Preventive checks can surface issues while they're still small." },
  { title: "Simplify technical support", text: "One local partner for your ongoing technology needs." },
  { title: "Maintain business equipment", text: "Keep computers, printers and networks in better working order." },
  { title: "Have a local technical partner", text: "On-site support from a team that knows your setup." },
];

const AREAS = [
  { icon: Monitor, label: "Computer maintenance" },
  { icon: Printer, label: "Printer support" },
  { icon: Network, label: "Network maintenance" },
  { icon: Cctv, label: "CCTV maintenance" },
  { icon: ClipboardCheck, label: "Preventive maintenance" },
  { icon: Wrench, label: "On-site technical support" },
];

export default function Amc() {
  const [mainBenefits, lastBenefitRow] = splitRows(BENEFITS, 3);
  return (
    <>
      <Seo
        title="AMC — Coming Soon | Cherians Tech Solutions"
        siteName={COMPANY.brand}
        description="CTS is preparing Annual Maintenance Contracts for businesses and institutions — computer, printer, network and CCTV maintenance. Register your interest."
        jsonLd={breadcrumbJsonLd([{ label: "AMC" }])}
      />
      <PageHero
        eyebrow="AMC"
        title="AMC"
        intro="We're preparing AMC plans designed to help businesses and institutions maintain their computers, printers, networking and other technology infrastructure."
        crumbs={[{ label: "AMC" }]}
        badge="COMING SOON"
      />
      <section className="section-pad bg-white">
        <div className="container-site">
          <SectionHead eyebrow="WHY AMC" title="Planned Maintenance, Fewer Surprises" />
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {mainBenefits.map((b, i) => (
              <Reveal key={b.title} delay={i * 60}>
                <div className="card-base h-full p-6" data-testid={`amc-benefit-${i}`}>
                  <ShieldCheck size={24} strokeWidth={1.75} className="text-cts-red mb-4" />
                  <h2 className="font-heading font-bold text-cts-ink text-lg mb-2">{b.title}</h2>
                  <p className="text-[15px] text-cts-charcoal leading-relaxed">{b.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          {lastBenefitRow.length > 0 && (
            <div className="flex flex-wrap justify-center gap-5 mt-5">
              {lastBenefitRow.map((b, i) => (
                <div key={b.title} className="w-full sm:w-[calc(50%-10px)]" style={{ maxWidth: CARD_MAX_W[3] }}>
                  <Reveal delay={(mainBenefits.length + i) * 60}>
                    <div className="card-base h-full p-6" data-testid={`amc-benefit-${mainBenefits.length + i}`}>
                      <ShieldCheck size={24} strokeWidth={1.75} className="text-cts-red mb-4" />
                      <h2 className="font-heading font-bold text-cts-ink text-lg mb-2">{b.title}</h2>
                      <p className="text-[15px] text-cts-charcoal leading-relaxed">{b.text}</p>
                    </div>
                  </Reveal>
                </div>
              ))}
            </div>
          )}
          <div className="mt-12">
            <SectionHead eyebrow="PLANNED COVERAGE" title="Potential AMC Areas" />
            <div className="flex flex-wrap gap-3">
              {AREAS.map((a) => (
                <span key={a.label} className="inline-flex items-center gap-2 rounded-md border border-cts-line bg-cts-soft px-4 py-2.5 font-heading font-bold text-[13px] uppercase tracking-wide text-cts-charcoal">
                  <a.icon size={16} className="text-cts-red" /> {a.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>
      <section id="register" className="section-pad bg-cts-redlight">
        <div className="container-site">
          <div className="max-w-xl mx-auto rounded-xl border border-cts-line bg-white shadow-[0_24px_60px_-40px_rgba(23,27,30,0.35)] p-6 md:p-9" data-testid="amc-form-card">
            <p className="eyebrow mb-2">REGISTER YOUR INTEREST</p>
            <h2 className="h3-display mb-2">Register Your Interest in AMC</h2>
            <p className="text-cts-charcoal text-[15px] mb-6">
              Tell us about your setup and we'll reach out when AMC plans launch. No pricing or
              packages are published yet — registering simply lets us plan with you in mind.
            </p>
            <LeadForm
              formType="amc"
              requirement="AMC — Register Interest"
              fields={["name", "phone", "organization", "location"]}
              customFields={[
                { name: "num_computers", label: "Number of computers", type: "number" },
                { name: "num_printers", label: "Number of printers", type: "number" },
                { name: "networking", label: "Do you have office networking?", type: "select", options: ["Yes", "No"] },
                { name: "cctv", label: "Do you have CCTV?", type: "select", options: ["Yes", "No"] },
                { name: "additional", label: "Additional requirements", type: "textarea" },
              ]}
              submitLabel="REGISTER YOUR INTEREST"
              trackEvent="amc_interest_submitted"
              testIdPrefix="amc-form"
            />
          </div>
        </div>
      </section>
    </>
  );
}
